/**
 * Assemble the Cohesion-derived `trackingContext` object for exposure on the
 * Configurable Experiences interpolation context as `context.trackingContext`.
 *
 * This is the object-valued analog of {@link resolveGeolocation} (a scalar
 * pushed through DSM `formState`) and a sibling of {@link buildLeadContext}:
 * `trackingContext` is a nested object, so it lives on `flowContext`
 * (`context.*`) rather than DSM `formState`, which is `Record<string, string>`
 * and cannot carry structured values.
 *
 * A result/entry-page config references it in an offerings data source body via
 * `body: { trackingContext: "{{context.trackingContext}}" }`. The Offerings
 * `POST /v1/offerings/search` and `POST /v1/offerings/search/match` endpoints
 * accept an optional `trackingContext` (an opaque object) and forward it to
 * Delivery System (lead qualification), Weaver / Make (ML ranking +
 * `ProductsSorted` telemetry, which read `anonymousId` / `writeKey`), and
 * application-CTA interpolation (which reads `correlationId`).
 *
 * Shape (aligned with the Offerings service owner): forward the **entire**
 * Cohesion `webContext` as top-level properties of `trackingContext` (nested
 * values such as `page` / `cookieConsent` stay nested), then add:
 *   - `writeKey`      — Cohesion `tagular.writeKey`, falling back to the
 *     top-level `writeKey` (mirrors `getTrackingContext`).
 *   - `correlationId` — the app-level correlation id inherited from the
 *     surrounding `DynamicExperienceProvider` (passed in by the caller).
 *
 * `anonymousId` normally arrives via the `webContext` spread; if `webContext`
 * omitted it we fall back to the Cohesion root `anonymousId` (populated by
 * other flow/legacy code) and then the tagular alias set, so downstream
 * tracking joins survive when only the legacy fields are populated.
 *
 * Returns `undefined` when nothing is resolvable (SSR, Cohesion not yet
 * ready). Without this guard an empty object would still be truthy and force
 * every request to carry an empty `trackingContext`; skipping it keeps the key
 * absent so the request degrades cleanly to the offerings service's own
 * fallbacks. Callers bind the result as a single-expression token, so a
 * resolved object is forwarded verbatim and an `undefined` is dropped at
 * `JSON.stringify` time.
 *
 * Defensive by design: assembly must never break the surrounding `flowContext`
 * build. Any failure returns `undefined`.
 */
export function buildTrackingContext(
  correlationId: string | undefined,
): Record<string, unknown> | undefined {
  try {
    const cohesion =
      typeof window !== 'undefined' ? window._Cohesion : undefined;

    // Forward the whole webContext as top-level props; nested objects
    // (page, cookieConsent, ...) stay nested. `{ ...undefined }` is `{}`.
    const trackingContext: Record<string, unknown> = {
      ...cohesion?.webContext,
    };

    if (!trackingContext.anonymousId) {
      const aliasSet =
        typeof window !== 'undefined' && typeof window.tagular === 'function'
          ? (window.tagular('getAliasSet') as
              | { anonymousId?: string }
              | undefined)
          : undefined;
      const anonymousId = cohesion?.anonymousId || aliasSet?.anonymousId;
      if (anonymousId) trackingContext.anonymousId = anonymousId;
    }

    const writeKey = cohesion?.tagular?.writeKey || cohesion?.writeKey;
    if (writeKey) trackingContext.writeKey = writeKey;
    if (correlationId) trackingContext.correlationId = correlationId;

    return Object.keys(trackingContext).length > 0
      ? trackingContext
      : undefined;
  } catch {
    return undefined;
  }
}
